import {
  FeatureName,
  PermissionValues as Permissions,
  useIsFeatureEnabled,
  WorkspaceType,
} from '@gripp/shared-logic';
import { FC } from 'react';
import { AssetMenuItem } from '../../assets/components/assetsMenuItem';
import { NavLinkProps } from './menuLink';

export interface IMenu {
  titleKey: string;
  icon: string;
  route: string;
  requiredPermission: string;
  requiredFeature?: FeatureName;
  requiredWorkspaceType?: WorkspaceType;
  isWorkspaceRequired?: boolean;
  component?: FC<NavLinkProps>;
}

export enum Routes {
  AccountPage = 'account-page',
  Accounts = 'accounts',
  Activities = 'activities',
  Assets = 'assets',
  Groups = 'groups',
  Routines = 'routines',
  NewRoutines = 'new-routines',
  Tags = 'tags',
  TagBatches = 'tagbatches',
  Workspaces = 'workspaces',
  Features = 'features',
  Alerts = 'alerts',
  AlertsManagement = 'alerts-management',
  Products = 'products',
  Files = 'files',
  AddOns = 'add-ons',
}

export const routeNames = {
  [Routes.AccountPage]: `/account-page`,
  [Routes.Accounts]: `/accounts`,
  [Routes.Activities]: `/activities`,
  [Routes.Assets]: `/assets`,
  [Routes.Groups]: `/groups`,
  [Routes.Routines]: `/routines`,
  [Routes.NewRoutines]: `/new-routines`,
  [Routes.Products]: `/products`,
  [Routes.Files]: `/files`,
  [Routes.Tags]: `/tags`,
  [Routes.TagBatches]: `/tagbatches`,
  [Routes.Workspaces]: `/workspaces`,
  [Routes.Features]: `/features`,
  [Routes.Alerts]: `/alerts`,
  [Routes.AlertsManagement]: `/alerts-management`,
  [Routes.AddOns]: `/add-ons`,
};

const guestMenuItems = [
  {
    titleKey: 'activity.menu.title',
    icon: 'bell-outline',
    route: routeNames[Routes.Activities],
    requiredPermission: Permissions.activity.read,
  },
  {
    titleKey: 'assets.menu.title',
    icon: 'qrcode',
    route: routeNames[Routes.Assets],
    requiredPermission: Permissions.asset.read,
    component: AssetMenuItem,
  },
  {
    titleKey: 'admin.menu.admin',
    icon: 'account-multiple',
    route: routeNames[Routes.Accounts],
    requiredPermission: Permissions.peoplepage.read,
  },
];

const adminMenuItems = [
  {
    titleKey: 'routine.list.oldRoutines',
    icon: 'format-list-checks',
    route: routeNames[Routes.Routines],
    requiredPermission: Permissions.globalAdmin,
  },
  {
    titleKey: 'routine.list.routines',
    icon: 'format-list-checks',
    route: routeNames[Routes.NewRoutines],
    requiredPermission: [
      Permissions.globalAdmin,
      Permissions.routinespage.read,
    ],
    requiredFeature: FeatureName.NewRoutinesPage,
    isWorkspaceRequired: true,
  },
  {
    titleKey: 'tags.title',
    icon: 'tag',
    route: routeNames[Routes.Tags],
    requiredPermission: Permissions.globalAdmin,
  },
  {
    titleKey: 'tags.batches.title',
    icon: 'tag-multiple',
    route: routeNames[Routes.TagBatches],
    requiredPermission: Permissions.globalAdmin,
  },
  {
    titleKey: 'features.title',
    icon: 'test-tube',
    route: routeNames[Routes.Features],
    requiredPermission: Permissions.globalAdmin,
  },
  {
    titleKey: 'alerts.title',
    icon: 'bell-plus-outline',
    route: routeNames[Routes.Alerts],
    requiredPermission: Permissions.globalAdmin,
  },
  {
    titleKey: 'alertsManagement.menu.title',
    icon: 'bell-alert',
    route: routeNames[Routes.AlertsManagement],
    requiredPermission: Permissions.globalAdmin,
    isWorkspaceRequired: true,
  },
  {
    titleKey: 'admin.menu.workspaces',
    icon: 'group',
    route: routeNames[Routes.Workspaces],
    requiredPermission: Permissions.globalAdmin,
  },
];

const primaryAdminMenuItems = [
  {
    titleKey: 'groups.menu.title',
    icon: 'google-circles-extended',
    route: routeNames[Routes.Groups],
    requiredPermission: Permissions.grouppage.read,
    requiredFeature: FeatureName.GroupsPage,
    isWorkspaceRequired: true,
  },
  {
    titleKey: 'product.list.products',
    icon: 'tag',
    route: routeNames[Routes.Products],
    requiredPermission: Permissions.product.all,
    requiredWorkspaceType: WorkspaceType.Oem,
    isWorkspaceRequired: true,
  },
  {
    titleKey: 'addOn.list.addOns',
    icon: 'shape-outline',
    route: routeNames[Routes.AddOns],
    requiredPermission: Permissions.addon.all,
    requiredWorkspaceType: WorkspaceType.Oem,
    isWorkspaceRequired: true,
  },
  {
    titleKey: 'file.list.files',
    icon: 'file',
    route: routeNames[Routes.Files],
    requiredPermission: Permissions.filespage.read,
    requiredFeature: FeatureName.FilesPage,
    requiredWorkspaceType: WorkspaceType.Oem,
    isWorkspaceRequired: true,
  },
];

const supportMenuItems = [
  {
    titleKey: 'accountPage.menu.accountPage',
    icon: 'shield-key',
    route: routeNames[Routes.AccountPage],
    requiredPermission: Permissions.account.allWorkspace,
    requiredFeature: FeatureName.AccountPage,
  },
];

export const MenuItems: IMenu[][] = [
  primaryAdminMenuItems,
  guestMenuItems,
  adminMenuItems,
  supportMenuItems,
];
